import React from "react";
import CasestudyTemplate, {
  Header,
  Introduction,
  Challenge,
  Body,
} from "../../../../templates/casestudyTemplate";
import Section from "../../../../components/section";
import Gallery from "../../../../components/gallery";

const TLDR = (
  <>
    <p>
      Using <strong>a methodical approach</strong> to identify pain points and
      optimise the journey I conducted usability testing which highlighted
      actionable themes. I facilitated an ideation workshop with team to ensure
      stakeholders were taken on the journey with me. A new design was created
      and prototyped which went through two further rounds of usability testing.
      Through clear and objective research I got buy in from Senior Stakeholders
      to run an A/B test.
    </p>
    <h3>Results:</h3>
    <ul>
      <li>
        <strong>Increased contract renewals by 29%</strong> through improved UX
        and messaging.
      </li>
      <li>
         <strong>Boosted key product adoption by 56%</strong>, ensuring more
        customers chose value-packed bundles.
      </li>
      <li>
        <strong>Reduced fallout to 9.4% </strong> from 19.2%, resulting in fewer
        calls to the contact centre.
      </li>
      <li>
        <strong>Increased fibre activation from 45% to 100% </strong> - a
        whopping 122% increase! This was a suprise result that had significent
        knock-on effects. Ask me about this at inverview!
      </li>
    </ul>
    <p>I'll let Jamie Lakritz, former Head of Product at SSE, sum up.</p>
    <Gallery
      id={"ctc"}
      images={[
        {
          source: "jamie_feedback.png",
          alt: "Slack messages from Jamie, Former head of Product at SSE",
        },
      ]}
      options={{ grid: 1, width: "inline", includeBackground: false }}
    />
  </>
);

export default function IndexPage() {
  return (
    <CasestudyTemplate id="ctc">
      <Header id="ctc" />
      <Introduction id="ctc">
        <p>
          As products and services evolve, the way companies communicate their
          value must evolve too.
        </p>
        <p>
          Contract renewals are a critical but often overlooked touchpoint, yet
          optimising them is a proven way to increase customer lifetime value.
        </p>
        <p>
          Working as part of the SSE Digital Studio I partner with the Customer
          Tariff Change product team to identify pain points and find
          optimisations within their journey.
        </p>
      </Introduction>
      <Challenge id="ctc">
        <p>
          At <strong>SSE</strong>, a major energy provider, selling bundled
          products - energy + <span className="italic">something else</span> -
          was a core priority. They were also the most value-for-money products
          for consumers benefiting form steep discounts. However, the contract
          renewal journey historically had a low conversion rate and failed to
          highlight the benefits of switching to these products. Product
          adoption was the main KPI but if I could do something about the
          conversion rate too, then that would be a bonus.
        </p>
      </Challenge>
      <Body id="ctc" TLDR={TLDR}>
        <Section>
          <h3>First things first, what is Customer Tariff Change?</h3>
          <p>
            CTC, or Customer Tariff Change is the name given to the product team
            who deals with the customer experience around tariff changes. When
            you buy energy from an energy company, typically you fix your energy
            needs (monthly cost) for a period of time. When that fixed period
            comes to an end, you have three choices: fix again with the same
            company, move onto the standard variable tariff, or move to a new
            energy supplier.
          </p>
          <h3>What does the data say?</h3>
          <p>
            I had assumptions, as most designers and stakeholders do when
            working on products or services but as the diligent designer that I
            am, I didn’t want to be led in the wrong direction by my own biases.
            I needed more data!
          </p>
          <p>
            For a moment though, lets explore my initial thoughts: My assumption
            was that the people who enter this journey are motivated by pricing
            and value for money. If they don’t see an offering that aligns with
            their interpretation they’ll look elsewhere for better deals. Often
            product comparison sites.
          </p>
          <p>
            The journey itself isn’t particularly long or complicated:
            <ol>
              <li>User follows link in email</li>
              <li>User verifies a few details</li>
              <li>Gets presented with their renewal options</li>
              <li>Confirms choice and completes journey.</li>
            </ol>
          </p>
          <p>
            Logically, I assumed that most people were dropping off at step 3
            and that it’s probably a combination of us not selling well enough
            (communicating the consumer value) and the cultural norms of
            shopping around. We’re not going to be able to do anything about the
            latter, but the former is something I can help with.
          </p>
          <ul>
            <li>The conversion rate for this journey sat at 15%</li>
            <li>The drop off rate for step 3 was significant</li>
            <li>
              Fallout sat at 19.2% -
              <p className="italic">
                Fallout is the percentage of people who are unable to complete
                the journey because of eligibility or other errors. In these
                cases the user is prompted to call the contact centre.
              </p>
            </li>
          </ul>
          <p>
            Having this information is great, it focuses my attention on the
            problem areas but it only tells me what is happening. To make
            improvements, I need to know why.
          </p>
          <ul>
            <li>Why are users dropping off at this stage?</li>
            <li>Do users understand the value of “bundled” products?</li>
            <li>What information do they need to make an informed decision?</li>
            <li>
              Are we providing that information in a clear and convenient way?
            </li>
          </ul>
          <p>
            Having access to remote usability testing tools like
            [usertesting.net](http://usertesting.net) or userzoom meant I was
            quickly able to create and deploy research studies to get the
            answers that I needed.
          </p>
          <p>
            To ensure I tested with the right people I created a screener to
            find participants who actively manage the energy needs of their
            house hold, were based in the uk, and I sought an equal split of
            genders and age ranges.
          </p>
          <p>
            Using a basic prototype I simply prompted the participants to
            imagine that their energy contract was up for renewal and their task
            was to choose a product that felt offered them the best value for
            money.
          </p>
        </Section>
        <Gallery
          id={"ctc"}
          images={[
            {
              source: "ut1_testingToolScreenshot.png",
              alt: "Usertesting.com participant list.",
            },
            {
              source: "ut1_spreadsheet.png",
              alt: "Analysis of sessions using Excel",
            },
          ]}
          options={{ grid: 2, width: "full", includeBackground: true }}
        />
        <Section>
          <p>
            The usability testing highlighted several themes that were
            contributing to the lower conversion rate and causing usability
            issues.
          </p>
          <p>The themes were:</p>
          <ul>
            <li>Misleading or confusing pricing</li>
            <li>Ease of comparing their current deal with the new offers</li>
            <li>Product explanations</li>
            <li>
              Skepticism of the broadband offering - A non traditional product
              being offered by an energy company.
            </li>
          </ul>
          <p>
            With these lenses the issues with the old page are clear to see.
            Just highlighting every cost on the page clearly shows how confusing
            the design is.
          </p>
        </Section>
        <Gallery
          id={"ctc"}
          images={[
            {
              source: "prices.png",
              alt: "The original design with every price highlighted in red.",
            },
          ]}
          options={{ grid: 1, width: "inline", includeBackground: false }}
        />
        <Section>
          <p>
            My takeaway was that this whole page needs redesigning to address
            the themes I found. We needed to focus on simplifying the experience
            to allow the user to to compare the new offer to their existing
            offer, we needed to simplify the product names/explanation, and
            simplify the pricing.
          </p>
          <p>
            At this point, I needed to take the findings back to the team and
            get buy in for the changes I wanted to explore.
          </p>
          <h3>Bringing the team on the journey with me</h3>
          <p>
            Starting with a playback deck, I took the PO and Head of Product
            though my findings and we agreed to hold a workshop with the product
            team to review what we’ve learnt and ideate ways we can address the
            issues.
          </p>
        </Section>
        <Gallery
          id={"ctc"}
          images={[
            {
              source: "playback.png",
              alt: "Playback deck in Google Slides communicating the usability test findings.",
            },
          ]}
          options={{ grid: 1, width: "full", includeBackground: false }}
        />
        <Section>
          <p>
            I enjoy running workshops, and in order to make them more engaging
            and memorable for the product team I always choose a theme. In this
            case the theme was an island adventure.
          </p>
        </Section>
        <Gallery
          id={"ctc"}
          images={[
            {
              source: "workshop1.png",
              alt: "Miro board with travel themed exercises.",
            },
            {
              source: "workshop2.png",
              alt: "Miro board with travel themed exercise full of sticky notes.",
            },
          ]}
          options={{ grid: 2, width: "full", includeBackground: false }}
        />
        <Section>
          <p>
            After recapping the themes, explaining the agenda and visiting
            Ice-Breaker Island, the main activity was Good Idea - Bad Idea.
          </p>
          <p>
            The team knew the themes we were addressing so I set them the task
            to come up with ways we can make the experience worse for our users.
          </p>
          <ul>
            <li>How do we make the pricing more confusing?</li>
            <li>How do we make it harder to compare deals?</li>
            <li>ow do we make this as frustrating an experience as possible</li>
          </ul>
          <p>
            This exercise is a great way to to get all teams members to
            collaborate. It’s often daunting to come up with good ideas on the
            spot but anyone can come up with a bad idea!
          </p>
          <p>
            Once we had a good set of bad ideas we flipped them on their heads
            and used them as inspiration for good ideas. “Drown the user in
            copy” became “Clearly and concisely sum up the benefits of each
            tariff.”
          </p>
          <p>
            The team really enjoyed this and valued the opportunity to be
            involved in coming up with solutions.
          </p>
          <h3>Ideation, wireframing, and visual design</h3>
          <p>
            I was sure that less-is-more was the way to go here. There is so
            much information we could show the consumer but not all of the
            information is needed at the same time and not all the information
            has the same value. I strongly believe that if content isn’t serving
            a need then it should removed. By limiting the information and
            revealing it to the user as and when they need it, I would ensure
            the the user didn’t get overloaded and the information landed when
            it did get seen.
          </p>
          <p>
            I started by removing all imagery and superfluous copy. I stripped
            back the product names to their core offering, deleted any sales
            copy, and removed all pricing that wasn’t the final value the
            consumer would be charged.
          </p>
        </Section>
        <Gallery
          id={"ctc"}
          images={[
            {
              source: "productCards_front.png",
              alt: "Side by side comparison of all product cards.",
            },
          ]}
          options={{ grid: 1, width: "inline", includeBackground: false }}
        />
        <Section>
          <p>
            This stripped back set of information fit very neatly onto cards and
            provided a clear foundational hierarchy of information: product
            name, contract length, and price. With this hierarchy all tariffs
            could be presented consistently and comparisons should be much
            easier.
          </p>
          <p>
            When adding back in information I had to weigh up whether it would
            disrupt the intended hierarchy, and would it add value for the
            consumer to be informed of it at that stage.
          </p>
          <p>
            With these guidelines I added a few more elements. A limited number
            of product tags to clearly communicate the unique elements of each
            tariff as the official product names could still be a bit confusing,
            the original price before any discounts had been applied (a legal
            requirement), and a single line detailing the discount.
          </p>
          <p>
            To further aid comparisons I subtly reinforced the model of a
            bundled product, energy plus something else, by including product
            icons. These were already in use elsewhere by the brand so it was a
            nice opportunity for some brand consistency
          </p>
          <p>
            The product cards would be presented in a grid and on smaller
            screens they would scroll horizontally to keep all information at
            the same level.
          </p>
        </Section>
        <Gallery
          id={"ctc"}
          images={[
            {
              source: "newDesign_large.png",
              alt: "Large screen layout of product cards.",
            },
          ]}
          options={{ grid: 1, width: "inline", includeBackground: false }}
        />
        <Section>
          <p>
            When a user did open a card they were presented with more
            at-a-glance summaries of the various elements that make up their
            tariff. We had to explain what was included in each part of the
            tariff and what the terms and conditions were. Again, this is a lot
            of information and didn’t need to presented all at once so each
            element had it’s own “See more information” button.
          </p>
        </Section>
        <Gallery
          id={"ctc"}
          images={[
            {
              source: "cards_open.png",
              alt: "Product card modals detailing product information.",
            },
          ]}
          options={{ grid: 1, width: "inline", includeBackground: false }}
        />
        <Section>
          <p>
            For the broadband and boiler protection bundled products I felt it
            was important to introduce some additional imagery to drive home the
            unique elements of those tariffs.
          </p>
        </Section>
        <Gallery
          id={"ctc"}
          images={[
            {
              source: "feature_cards.png",
              alt: "Cards showing product USPs.",
            },
          ]}
          options={{ grid: 1, width: "inline", includeBackground: false }}
        />
        <Section>
          <p>
            Particularly broadband; One of the findings from usability testing
            testing was that users were not confident that the broadband package
            could be installed at their properties, that they might not have the
            right infrastructure, despite the fact that we had already, behind
            the scenes, verified tht they could. They wouldn’t be presented with
            products that we couldn’t fulfil.
          </p>
          <p>
            To address this issue, I included a map of the users location and a
            simple piece of copy which read: Fibre broadband to
            <span>"first line of address."</span> This subtle piece of micro
            copy I hoped would be enough to reassure those users.
          </p>
          <h3>Validating the design</h3>
          <p>
            This design was very different from the original. Lots of change, a
            completely different approach to communication, and the product
            owner was nervous.
          </p>
          <p>
            I went back to usertesting.com and spun up a test. I used the same
            scenario and screener as last time and build a basic prototype of
            the the card interaction.
          </p>
          <p>
            This time, there were a few things I specifically wanted to
            validate:
          </p>
          <ol>
            <li>
              Did the user engage with the horizontal scroll? - This was a major
              concern for the product owner. On small screens when the page
              initially loads the user would only see one tariff card. Is this
              going to confuse people? Will people see the other products?
            </li>
            <li>
              Which tariff do participants think offers the best value for
              money.
            </li>
            <li>How did they compare each tariff</li>
            <li>
              Was there any information missing that they were expecting to see
            </li>
          </ol>
        </Section>
        <Gallery
          id={"ctc"}
          images={[
            {
              source: "ut2_screenshot.png",
              alt: "Usertesting.com participant list.",
            },
            {
              source: "ut2_analysis.png",
              alt: "Analysis of usability testing using Miro.",
            },
          ]}
          options={{ grid: 2, width: "full", includeBackground: true }}
        />
        <Section>
          <p>The results were overwhelming...</p>
          <p>
            The reduced set of information, and the delivery of more information
            via progressive disclosure did not have any effect on the
            understanding of what each product offered and none of the original
            concerns were raised during this testing round.
          </p>
        </Section>
        <Gallery
          id={"ctc"}
          images={[
            {
              source: "participant_quote_2.png",
            },
            {
              source: "participant_quote_3.png",
            },
          ]}
          options={{ grid: 1, width: "inline", includeBackground: false }}
        />
        <Section>
          <p>
            5 out of 10 participants used the “Find our more” link to explore in
            more detail what each tariff offered. The remaining 5 participants
            didn’t explore the tariffs in more detail, however due to the
            limitations of the prototype, the “find out more” link was hidden
            from view. Of these 5, 3 said they would expect to see more
            information on the following screens.
          </p>
          <p>
            Most participants choose one of the bundled products when asked to
            choose a tariff they thought represented the best value for money
            which is marked improvement when compared to the first test.
          </p>
          <p>
            There were a few minor things I wanted to tweak but I was confident
            with my designs and we should move forward with this direction.
          </p>

          <h3>Outcomes</h3>
        </Section>
        <Gallery
          id={"ctc"}
          images={[
            {
              source: "journey.png",
              alt: "Slide showing the activities undertaken to get a dev ready design.",
            },
          ]}
          options={{ grid: 1, width: "inline", includeBackground: false }}
        />
        <Section>
          <p>
            After presenting the current status of the project on a company all
            hands, including how I’d got to where we are, which I did with a
            single slide outlining the journey, I got the go ahead from the head
            of product to implement an A/B test. This is where the real proof
            would come from.
          </p>
          <p>The results:</p>
        </Section>
        <Gallery
          id={"ctc"}
          images={[
            {
              source: "summary.png",
              alt: "Slide detailing the results of the A/B test, presented by the product owner.",
            },
          ]}
          options={{ grid: 1, width: "inline", includeBackground: false }}
        />
        <Section>
          <ul>
            <li>Overall conversion increased by 29% from 15% to 19.4%.</li>
            <li>Bundled product adoption increased by 56%.</li>
            <li>
              Fibre activation increased from 45% to 100% - a whopping 122%
              increase! This is particularly interesting side effect and it’s
              worth digging into a bit more.
            </li>
          </ul>
          <p>
            It turned out that 55% of people who choose the Fibre broadband
            package in the original design did so by mistake or because it had
            the biggest discount but they didn’t intend to activate the
            broadband element at all - a condition of the tariff. Those people
            would have been moved onto the standard variable tariff when they
            canceled the installation.
          </p>
          <p>
            So to hit 100% activation with the new design meant that everybody
            who choose the Fiber product wanted it because it was the right
            product for them and not because they chose it by mistake or other
            cheeky reasons.
          </p>
          <p>
            This outcome was a complete surprise to everyone but the knock on
            affects were not small. Reduced calls to the call centre, reduced
            failed installation attempt by engineers, and all in all happier
            customers.
          </p>
          <p>
            I'll let Jamie Lakritz, former Head of Product at SSE, finish up.
          </p>
          <Gallery
            id={"ctc"}
            images={[
              {
                source: "jamie_feedback.png",
                alt: "Slack messages from Jamie, Former head of Product at SSE",
              },
            ]}
            options={{ grid: 1, width: "inline", includeBackground: false }}
          />
        </Section>
      </Body>
    </CasestudyTemplate>
  );
}
